import Modal from 'react-bootstrap/Modal';
import React, { useState, useEffect, useContext } from 'react';

import 'handsontable/dist/handsontable.full.min.css';

import { registerAllModules } from 'handsontable/registry';

import { useAuth } from 'react-oidc-context';
import { SnackbarContext } from '../../../App';

registerAllModules();

export default ({ show, onHide, selectedDocument, onShow }) => {
    const auth = useAuth();

    const [anchorEl, setAnchorEl] = useState(null);

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const [editedFileName, setEditedFileName] = useState("");
    const [editedFileNameReasoning, setEditedFileNameReasoning] = useState("");
    const [editedFolderLevels, setEditedFolderLevels] = useState([]);

    const [originalFileName, setOriginalFileName] = useState("");
    const [originalFileNameReasoning, setOriginalFileNameReasoning] = useState("");
    const [originalFolderLevels, setOriginalFolderLevels] = useState([]);

    const [isEditingFileName, setIsEditingFileName] = useState(false);
    const [isEditingFolderPath, setIsEditingFolderPath] = useState(false);
    const [isMovingFile, setIsMovingFile] = useState(false);

    const {
        snackBarShow,
        setSnackBarShow,
        snackBarMessage,
        setSnackBarMessage,
        sessionExpired,
        setSessionExpired
    } = useContext(SnackbarContext);

    const normaliseText = (value) => {
        return String(value || "").trim();
    };

    const generateLevelUid = () => {
        return `level_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    };

    const getFileName = (path) => {
        if (!path) return "Unknown file";
        return path.split("/").filter(Boolean).pop() || path;
    };

    const parsePathReasoning = (value) => {
        if (!value) return [];

        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch (err) {
            return [];
        }
    };

    const splitFolderPath = (path) => {
        if (!path) return [];

        return path
            .split("/")
            .filter(Boolean)
            .map((folderName, index) => ({
                uid: generateLevelUid(),
                original_uid: null,
                level: index + 1,
                folder_name: folderName,
                confidence: "Unknown",
                evidence_type: "Unknown",
                reasoning: ""
            }));
    };

    const buildFolderPath = (levels) => {
        const path = levels
            .map(item => String(item.folder_name || "").trim())
            .filter(Boolean)
            .join("/");

        return path ? `/${path}` : "";
    };

    const mergeSuggestedPathWithReasoning = (suggestedPath, reasoningArray) => {
        const pathLevels = splitFolderPath(suggestedPath);

        return pathLevels.map((pathLevel) => {
            const matchingReasoning = reasoningArray.find(
                item => Number(item.level) === Number(pathLevel.level)
            );

            const uid = generateLevelUid();

            return {
                ...pathLevel,
                uid,
                original_uid: uid,
                confidence: matchingReasoning?.confidence || "Unknown",
                evidence_type: matchingReasoning?.evidence_type || "Unknown",
                reasoning: matchingReasoning?.reasoning || ""
            };
        });
    };

    const renumberFolderLevels = (levels) => {
        return levels.map((item, index) => ({
            ...item,
            level: index + 1
        }));
    };

    const getNewFolderLevel = () => ({
        uid: generateLevelUid(),
        original_uid: null,
        level: 0,
        folder_name: "",
        confidence: "User Edited",
        evidence_type: "User Input",
        reasoning: "Folder level manually added by user."
    });

    const addFolderLevelAfter = (index) => {
        setEditedFolderLevels(prev => {
            const newLevel = getNewFolderLevel();

            const updated = [
                ...prev.slice(0, index + 1),
                newLevel,
                ...prev.slice(index + 1)
            ];

            return renumberFolderLevels(updated);
        });

        setIsEditingFolderPath(true);
    };

    const addFolderLevelAtEnd = () => {
        setEditedFolderLevels(prev => {
            const newLevel = getNewFolderLevel();
            return renumberFolderLevels([...prev, newLevel]);
        });

        setIsEditingFolderPath(true);
    };

    const deleteFolderLevel = (index) => {
        setEditedFolderLevels(prev => {
            const updated = prev.filter((_, itemIndex) => itemIndex !== index);
            return renumberFolderLevels(updated);
        });
    };

    const getConfidenceStyle = (confidence) => {
        switch (confidence) {
            case "High":
                return {
                    background: "rgba(46, 204, 113, 0.12)",
                    color: "#1f9d55",
                    border: "0.05vw solid rgba(46, 204, 113, 0.35)"
                };
            case "Medium":
                return {
                    background: "rgba(241, 196, 15, 0.14)",
                    color: "#b7791f",
                    border: "0.05vw solid rgba(241, 196, 15, 0.4)"
                };
            case "Low":
                return {
                    background: "rgba(231, 76, 60, 0.12)",
                    color: "#c0392b",
                    border: "0.05vw solid rgba(231, 76, 60, 0.35)"
                };
            case "User Edited":
                return {
                    background: "rgba(37, 99, 235, 0.10)",
                    color: "#2563eb",
                    border: "0.05vw solid rgba(37, 99, 235, 0.28)"
                };
            default:
                return {
                    background: "rgba(108, 117, 125, 0.12)",
                    color: "#6c757d",
                    border: "0.05vw solid rgba(108, 117, 125, 0.3)"
                };
        }
    };

    const getStatusStyle = (status) => {
        const normalisedStatus = String(status || "").toLowerCase();

        if (normalisedStatus === "review") {
            return {
                background: "rgba(46, 204, 113, 0.12)",
                color: "#1f9d55",
                border: "0.05vw solid rgba(46, 204, 113, 0.35)"
            };
        }

        if (normalisedStatus === "queued") {
            return {
                background: "rgba(241, 196, 15, 0.14)",
                color: "#b7791f",
                border: "0.05vw solid rgba(241, 196, 15, 0.4)"
            };
        }

        return {
            background: "rgba(108, 117, 125, 0.12)",
            color: "#6c757d",
            border: "0.05vw solid rgba(108, 117, 125, 0.3)"
        };
    };

    const isProcessed = String(selectedDocument?.status || "").toLowerCase() === "review";
    const editedSuggestedPath = buildFolderPath(editedFolderLevels);

    useEffect(() => {
        const parsedReasoning = parsePathReasoning(selectedDocument?.ai_path_reasoning);

        const initialFileName = selectedDocument?.suggested_name || "";
        const initialFileNameReasoning = selectedDocument?.ai_name_reasoning || "";
        const initialFolderLevels = mergeSuggestedPathWithReasoning(
            selectedDocument?.suggested_path,
            parsedReasoning
        );

        setEditedFileName(initialFileName);
        setEditedFileNameReasoning(initialFileNameReasoning);
        setEditedFolderLevels(initialFolderLevels);

        setOriginalFileName(initialFileName);
        setOriginalFileNameReasoning(initialFileNameReasoning);
        setOriginalFolderLevels(initialFolderLevels);

        setIsEditingFileName(false);
        setIsEditingFolderPath(false);
        setIsMovingFile(false);
    }, [selectedDocument]);

    const updateFolderLevel = (index, field, value) => {
        setEditedFolderLevels(prev =>
            prev.map((item, itemIndex) =>
                itemIndex === index
                    ? {
                        ...item,
                        [field]: value,
                        confidence: field === "folder_name" ? "User Edited" : item.confidence,
                        evidence_type: field === "folder_name" ? "User Input" : item.evidence_type
                    }
                    : item
            )
        );
    };

    const getOriginalLevelByUid = (level) => {
        if (!level?.original_uid) return null;

        return originalFolderLevels.find(
            originalLevel => originalLevel.original_uid === level.original_uid
        ) || null;
    };

    const buildFolderLevelPayload = () => {
        return editedFolderLevels.map((level) => {
            const originalLevel = getOriginalLevelByUid(level);

            const levelAdded = !originalLevel;

            const folderNameEdited =
                levelAdded ||
                normaliseText(level.folder_name) !== normaliseText(originalLevel?.folder_name);

            const reasoningEdited =
                levelAdded ||
                normaliseText(level.reasoning) !== normaliseText(originalLevel?.reasoning);

            const levelPositionChanged =
                !levelAdded &&
                Number(level.level) !== Number(originalLevel?.level);

            return {
                uid: level.uid,
                original_uid: level.original_uid,

                level: level.level,
                folder_name: level.folder_name,
                confidence: level.confidence,
                evidence_type: level.evidence_type,
                reasoning: level.reasoning,

                original_level: originalLevel?.level ?? null,
                original_folder_name: originalLevel?.folder_name ?? null,
                original_confidence: originalLevel?.confidence ?? null,
                original_evidence_type: originalLevel?.evidence_type ?? null,
                original_reasoning: originalLevel?.reasoning ?? null,

                user_edited: folderNameEdited || reasoningEdited || levelAdded || levelPositionChanged,
                folder_name_edited: folderNameEdited,
                reasoning_edited: reasoningEdited,
                level_added: levelAdded,
                level_deleted: false,
                level_position_changed: levelPositionChanged
            };
        });
    };

    const buildDeletedFolderLevelPayload = () => {
        return originalFolderLevels
            .filter(originalLevel => {
                return !editedFolderLevels.some(
                    editedLevel => editedLevel.original_uid === originalLevel.original_uid
                );
            })
            .map(originalLevel => ({
                uid: originalLevel.uid,
                original_uid: originalLevel.original_uid,

                level: null,
                folder_name: null,
                confidence: null,
                evidence_type: null,
                reasoning: null,

                original_level: originalLevel.level,
                original_folder_name: originalLevel.folder_name,
                original_confidence: originalLevel.confidence,
                original_evidence_type: originalLevel.evidence_type,
                original_reasoning: originalLevel.reasoning,

                user_edited: true,
                folder_name_edited: false,
                reasoning_edited: false,
                level_added: false,
                level_deleted: true,
                level_position_changed: false
            }));
    };

    const handleMoveAndRename = () => {
        if (isMovingFile) return;

        setIsMovingFile(true);
        setSnackBarMessage("Moving and renaming file...");
        setSnackBarShow(true);

        const finalFolderLevelPayload = buildFolderLevelPayload();
        const deletedFolderLevelPayload = buildDeletedFolderLevelPayload();

        const fileNameEdited =
            normaliseText(editedFileName) !== normaliseText(originalFileName);

        const fileNameReasoningEdited =
            normaliseText(editedFileNameReasoning) !== normaliseText(originalFileNameReasoning);

        const payload = {
            id: selectedDocument?.id,
            ssid: selectedDocument?.ssid,

            original_path: selectedDocument?.path,
            original_file_name: getFileName(selectedDocument?.path),

            original_suggested_path: selectedDocument?.suggested_path || "",
            original_suggested_name: originalFileName,
            original_file_name_reasoning: originalFileNameReasoning,

            final_suggested_path: editedSuggestedPath,
            final_suggested_name: editedFileName,
            user_file_name_reasoning: editedFileNameReasoning,

            file_name_user_edited: fileNameEdited || fileNameReasoningEdited,
            file_name_edited: fileNameEdited,
            file_name_reasoning_edited: fileNameReasoningEdited,

            user_folder_level_reasoning: finalFolderLevelPayload,
            deleted_folder_levels: deletedFolderLevelPayload,

            folder_path_user_edited:
                finalFolderLevelPayload.some(item => item.user_edited) ||
                deletedFolderLevelPayload.length > 0
        };

        fetch(`${window.origin}/api/dochero/move`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${auth.user?.access_token}`
            },
            body: JSON.stringify(payload)
        }).then((response) => {
            if (response.status === 403) {
                setSessionExpired(true);
                throw new Error("Session expired");
            }

            if (!response.ok) {
                throw new Error(`Failed to move file. Status: ${response.status}`);
            }

            return response.json();
        }).then((data) => {
            setSnackBarMessage("File moved and renamed successfully.");
            setSnackBarShow(true);
            setIsMovingFile(false);
            onHide();
        }).catch((error) => {
            console.error(error);
            setSnackBarMessage("Failed to move file.");
            setSnackBarShow(true);
            setIsMovingFile(false);
        });
    };

    const modalShellStyle = {
        height: "85vh",
        maxHeight: "85vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#f8fafc",
        borderRadius: "1.2vw"
    };

    const headerStyle = {
        flex: "0 0 auto",
        padding: "1.15vw 1.35vw",
        borderBottom: "0.05vw solid #e5e7eb",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)"
    };

    const scrollBodyStyle = {
        flex: "1 1 auto",
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "1.25vw",
        background: "#f8fafc"
    };

    const footerStyle = {
        flex: "0 0 auto",
        display: "flex",
        justifyContent: "flex-end",
        gap: "0.52vw",
        padding: "0.85vw 1.25vw",
        borderTop: "0.05vw solid #e5e7eb",
        background: "#ffffff"
    };

    const cardStyle = {
        background: "#ffffff",
        border: "0.05vw solid #e5e7eb",
        borderRadius: "0.85vw",
        padding: "0.95vw",
        boxShadow: "0 0.42vw 1.25vw rgba(15, 23, 42, 0.04)"
    };

    const labelStyle = {
        fontSize: "0.62vw",
        color: "#6b7280",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.025vw",
        marginBottom: "0.42vw"
    };

    const inputStyle = {
        width: "100%",
        border: "0.05vw solid #d1d5db",
        borderRadius: "0.52vw",
        padding: "0.52vw 0.62vw",
        fontSize: "0.7vw",
        color: "#111827",
        outline: "none",
        background: "#ffffff"
    };

    const textareaStyle = {
        width: "100%",
        border: "0.05vw solid #d1d5db",
        borderRadius: "0.52vw",
        padding: "0.52vw 0.62vw",
        fontSize: "0.68vw",
        color: "#4b5563",
        outline: "none",
        resize: "vertical",
        lineHeight: 1.6,
        background: "#ffffff"
    };

    const smallActionButtonStyle = (active) => ({
        border: "0.05vw solid #d1d5db",
        background: active ? "#111827" : "#ffffff",
        color: active ? "#ffffff" : "#374151",
        borderRadius: "0.42vw",
        padding: "0.25vw 0.52vw",
        fontSize: "0.58vw",
        fontWeight: 800,
        cursor: "pointer"
    });

    const addButtonStyle = {
        border: "0.05vw solid #d1d5db",
        background: "#ffffff",
        color: "#374151",
        borderRadius: "0.42vw",
        padding: "0.25vw 0.52vw",
        fontSize: "0.58vw",
        fontWeight: 800,
        cursor: "pointer"
    };

    const deleteButtonStyle = {
        border: "0.05vw solid rgba(231, 76, 60, 0.35)",
        background: "rgba(231, 76, 60, 0.08)",
        color: "#c0392b",
        borderRadius: "0.38vw",
        padding: "0.18vw 0.38vw",
        fontSize: "0.55vw",
        fontWeight: 800,
        cursor: "pointer"
    };

    const miniAddButtonStyle = {
        border: "0.05vw solid #d1d5db",
        background: "#ffffff",
        color: "#374151",
        borderRadius: "0.38vw",
        padding: "0.18vw 0.38vw",
        fontSize: "0.55vw",
        fontWeight: 800,
        cursor: "pointer"
    };

    const isMoveButtonDisabled =
        isMovingFile || !isProcessed || !editedSuggestedPath || !editedFileName;

    return (
        <>
            <style>
                {`
                    .doc-hero-ai-review-modal {
                        width: 70vw !important;
                        max-width: 70vw !important;
                    }

                    .doc-hero-ai-review-modal .modal-content {
                        height: 85vh !important;
                        max-height: 85vh !important;
                        border-radius: 1.2vw !important;
                        overflow: hidden !important;
                        border: none !important;
                    }

                    .doc-hero-ai-review-modal .modal-body {
                        padding: 0 !important;
                        height: 85vh !important;
                        max-height: 85vh !important;
                        overflow: hidden !important;
                    }

                    .doc-hero-ai-review-modal input:focus,
                    .doc-hero-ai-review-modal textarea:focus {
                        border-color: #2563eb !important;
                        box-shadow: 0 0 0 0.15vw rgba(37, 99, 235, 0.12);
                    }
                `}
            </style>

            <Modal
                show={show}
                onHide={onHide}
                dialogClassName="doc-hero-ai-review-modal"
                aria-labelledby="example-custom-modal-styling-title"
                enforceFocus={false}
                restoreFocus={false}
                centered
                scrollable={false}
            >
                <Modal.Body>
                    <div style={modalShellStyle}>
                        <div style={headerStyle}>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: "0.85vw"
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            fontSize: "1.05vw",
                                            fontWeight: 800,
                                            color: "#111827",
                                            marginBottom: "0.2vw"
                                        }}
                                    >
                                        AI Filing Suggestion
                                    </div>

                                    <div
                                        style={{
                                            fontSize: "0.68vw",
                                            color: "#6b7280"
                                        }}
                                    >
                                        Review the suggested folder path and new file name before moving the file.
                                    </div>
                                </div>

                                <span
                                    style={{
                                        ...getStatusStyle(selectedDocument?.status),
                                        padding: "0.25vw 0.58vw",
                                        borderRadius: "999vw",
                                        fontSize: "0.62vw",
                                        fontWeight: 700,
                                        textTransform: "capitalize",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    {selectedDocument?.status || "Unknown"}
                                </span>
                            </div>
                        </div>

                        <div style={scrollBodyStyle}>
                            <div style={{ ...cardStyle, marginBottom: "0.95vw" }}>
                                <div style={labelStyle}>Original File</div>

                                <div
                                    style={{
                                        fontSize: "0.85vw",
                                        fontWeight: 800,
                                        color: "#111827",
                                        marginBottom: "0.32vw"
                                    }}
                                >
                                    {getFileName(selectedDocument?.path)}
                                </div>

                                <div
                                    style={{
                                        fontFamily: "monospace",
                                        fontSize: "0.62vw",
                                        color: "#6b7280",
                                        background: "#f3f4f6",
                                        borderRadius: "0.52vw",
                                        padding: "0.42vw 0.52vw",
                                        wordBreak: "break-all"
                                    }}
                                >
                                    {selectedDocument?.path || "No source path available"}
                                </div>
                            </div>

                            {!isProcessed ? (
                                <div
                                    style={{
                                        background: "#ffffff",
                                        border: "0.05vw dashed #d1d5db",
                                        borderRadius: "0.85vw",
                                        padding: "2.2vw 1.25vw",
                                        textAlign: "center"
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: "0.95vw",
                                            fontWeight: 800,
                                            color: "#111827",
                                            marginBottom: "0.42vw"
                                        }}
                                    >
                                        Suggestion not ready yet
                                    </div>

                                    <div
                                        style={{
                                            fontSize: "0.72vw",
                                            color: "#6b7280",
                                            maxWidth: "24vw",
                                            margin: "0 auto"
                                        }}
                                    >
                                        This file is currently queued or still processing. The suggested folder path,
                                        new file name, and AI reasoning will appear here once processing is complete.
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            gap: "0.95vw",
                                            marginBottom: "0.95vw"
                                        }}
                                    >
                                        <div style={cardStyle}>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    marginBottom: "0.42vw"
                                                }}
                                            >
                                                <div style={{ ...labelStyle, marginBottom: 0 }}>
                                                    Final Folder Path
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => setIsEditingFolderPath(prev => !prev)}
                                                    style={smallActionButtonStyle(isEditingFolderPath)}
                                                    disabled={isMovingFile}
                                                >
                                                    {isEditingFolderPath ? "Done" : "Edit Path"}
                                                </button>
                                            </div>

                                            <div
                                                style={{
                                                    fontFamily: "monospace",
                                                    fontSize: "0.68vw",
                                                    color: "#111827",
                                                    background: "#f3f4f6",
                                                    borderRadius: "0.52vw",
                                                    padding: "0.52vw 0.62vw",
                                                    lineHeight: 1.6,
                                                    wordBreak: "break-all",
                                                    marginBottom: "0.72vw"
                                                }}
                                            >
                                                {editedSuggestedPath || "Unknown"}
                                            </div>

                                            <div
                                                style={{
                                                    fontSize: "0.62vw",
                                                    color: "#6b7280",
                                                    lineHeight: 1.5
                                                }}
                                            >
                                                {isEditingFolderPath
                                                    ? "Edit, add, or delete folder levels below. The full path updates automatically."
                                                    : "Click Edit Path if you want to change any folder level or reasoning."}
                                            </div>
                                        </div>

                                        <div style={cardStyle}>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    marginBottom: "0.42vw"
                                                }}
                                            >
                                                <div style={{ ...labelStyle, marginBottom: 0 }}>
                                                    Final File Name
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => setIsEditingFileName(prev => !prev)}
                                                    style={smallActionButtonStyle(isEditingFileName)}
                                                    disabled={isMovingFile}
                                                >
                                                    {isEditingFileName ? "Done" : "Edit Name"}
                                                </button>
                                            </div>

                                            {!isEditingFileName ? (
                                                <>
                                                    <div
                                                        style={{
                                                            fontSize: "0.85vw",
                                                            fontWeight: 800,
                                                            color: "#111827",
                                                            marginBottom: "0.52vw",
                                                            wordBreak: "break-word"
                                                        }}
                                                    >
                                                        {editedFileName || "Unknown"}
                                                    </div>

                                                    <div
                                                        style={{
                                                            fontSize: "0.68vw",
                                                            color: "#4b5563",
                                                            lineHeight: 1.6
                                                        }}
                                                    >
                                                        {editedFileNameReasoning || "No file name reasoning available."}
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <input
                                                        value={editedFileName}
                                                        onChange={(e) => setEditedFileName(e.target.value)}
                                                        placeholder="Enter file name"
                                                        disabled={isMovingFile}
                                                        style={{
                                                            ...inputStyle,
                                                            fontWeight: 800,
                                                            marginBottom: "0.72vw"
                                                        }}
                                                    />

                                                    <div style={labelStyle}>
                                                        Reasoning for File Name
                                                    </div>

                                                    <textarea
                                                        value={editedFileNameReasoning}
                                                        onChange={(e) => setEditedFileNameReasoning(e.target.value)}
                                                        placeholder="Optional: explain why this file name is correct"
                                                        rows={4}
                                                        disabled={isMovingFile}
                                                        style={textareaStyle}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div style={cardStyle}>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                marginBottom: "0.72vw"
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: "0.78vw",
                                                    fontWeight: 800,
                                                    color: "#111827"
                                                }}
                                            >
                                                Folder Path Levels & Reasoning
                                            </div>

                                            {isEditingFolderPath && (
                                                <button
                                                    type="button"
                                                    onClick={addFolderLevelAtEnd}
                                                    style={addButtonStyle}
                                                    disabled={isMovingFile}
                                                >
                                                    + Add Level
                                                </button>
                                            )}
                                        </div>

                                        {editedFolderLevels.length > 0 ? (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "0.52vw"
                                                }}
                                            >
                                                {editedFolderLevels.map((item, index) => (
                                                    <div
                                                        key={item.uid || `${item.level}-${index}`}
                                                        style={{
                                                            border: "0.05vw solid #e5e7eb",
                                                            borderRadius: "0.72vw",
                                                            padding: "0.72vw",
                                                            background: item.folder_name === "Unknown" ? "#f9fafb" : "#ffffff"
                                                        }}
                                                    >
                                                        {!isEditingFolderPath ? (
                                                            <>
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        justifyContent: "space-between",
                                                                        alignItems: "flex-start",
                                                                        gap: "0.72vw",
                                                                        marginBottom: "0.42vw"
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: "0.42vw",
                                                                            flexWrap: "wrap"
                                                                        }}
                                                                    >
                                                                        <span
                                                                            style={{
                                                                                fontSize: "0.58vw",
                                                                                fontWeight: 800,
                                                                                color: "#374151",
                                                                                background: "#f3f4f6",
                                                                                border: "0.05vw solid #e5e7eb",
                                                                                borderRadius: "999vw",
                                                                                padding: "0.16vw 0.42vw"
                                                                            }}
                                                                        >
                                                                            Level {item.level}
                                                                        </span>

                                                                        <span
                                                                            style={{
                                                                                fontSize: "0.72vw",
                                                                                fontWeight: 800,
                                                                                color: item.folder_name === "Unknown" ? "#6b7280" : "#111827"
                                                                            }}
                                                                        >
                                                                            {item.folder_name}
                                                                        </span>
                                                                    </div>

                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                            flexDirection: "column",
                                                                            alignItems: "flex-end",
                                                                            gap: "0.26vw",
                                                                            minWidth: "6.2vw"
                                                                        }}
                                                                    >
                                                                        <span
                                                                            style={{
                                                                                ...getConfidenceStyle(item.confidence),
                                                                                padding: "0.16vw 0.47vw",
                                                                                borderRadius: "999vw",
                                                                                fontSize: "0.58vw",
                                                                                fontWeight: 800
                                                                            }}
                                                                        >
                                                                            {item.confidence}
                                                                        </span>

                                                                        <span
                                                                            style={{
                                                                                fontSize: "0.58vw",
                                                                                color: "#6b7280",
                                                                                textAlign: "right"
                                                                            }}
                                                                        >
                                                                            {item.evidence_type}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <div
                                                                    style={{
                                                                        fontSize: "0.68vw",
                                                                        color: "#4b5563",
                                                                        lineHeight: 1.6
                                                                    }}
                                                                >
                                                                    {item.reasoning || "No reasoning provided."}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div
                                                                    style={{
                                                                        display: "grid",
                                                                        gridTemplateColumns: "5vw 1fr 7vw 11vw 6.8vw",
                                                                        gap: "0.52vw",
                                                                        alignItems: "center",
                                                                        marginBottom: "0.52vw"
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontSize: "0.58vw",
                                                                            fontWeight: 800,
                                                                            color: "#374151",
                                                                            background: "#f3f4f6",
                                                                            border: "0.05vw solid #e5e7eb",
                                                                            borderRadius: "999vw",
                                                                            padding: "0.16vw 0.42vw",
                                                                            textAlign: "center"
                                                                        }}
                                                                    >
                                                                        Level {item.level}
                                                                    </span>

                                                                    <input
                                                                        value={item.folder_name || ""}
                                                                        onChange={(e) => updateFolderLevel(index, "folder_name", e.target.value)}
                                                                        placeholder="Folder name"
                                                                        disabled={isMovingFile}
                                                                        style={{
                                                                            ...inputStyle,
                                                                            fontWeight: 800
                                                                        }}
                                                                    />

                                                                    <span
                                                                        style={{
                                                                            ...getConfidenceStyle(item.confidence),
                                                                            padding: "0.16vw 0.47vw",
                                                                            borderRadius: "999vw",
                                                                            fontSize: "0.58vw",
                                                                            fontWeight: 800,
                                                                            textAlign: "center"
                                                                        }}
                                                                    >
                                                                        {item.confidence}
                                                                    </span>

                                                                    <span
                                                                        style={{
                                                                            fontSize: "0.58vw",
                                                                            color: "#6b7280",
                                                                            textAlign: "right"
                                                                        }}
                                                                    >
                                                                        {item.evidence_type}
                                                                    </span>

                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                            gap: "0.32vw",
                                                                            justifyContent: "flex-end"
                                                                        }}
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => addFolderLevelAfter(index)}
                                                                            title="Add folder level after this"
                                                                            style={miniAddButtonStyle}
                                                                            disabled={isMovingFile}
                                                                        >
                                                                            +
                                                                        </button>

                                                                        <button
                                                                            type="button"
                                                                            onClick={() => deleteFolderLevel(index)}
                                                                            title="Delete this folder level"
                                                                            style={deleteButtonStyle}
                                                                            disabled={isMovingFile}
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                <textarea
                                                                    value={item.reasoning || ""}
                                                                    onChange={(e) => updateFolderLevel(index, "reasoning", e.target.value)}
                                                                    placeholder="Optional: explain why this folder level is correct"
                                                                    rows={2}
                                                                    disabled={isMovingFile}
                                                                    style={textareaStyle}
                                                                />
                                                            </>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div
                                                style={{
                                                    fontSize: "0.68vw",
                                                    color: "#6b7280",
                                                    background: "#f9fafb",
                                                    border: "0.05vw dashed #d1d5db",
                                                    borderRadius: "0.62vw",
                                                    padding: "0.72vw"
                                                }}
                                            >
                                                <div style={{ marginBottom: isEditingFolderPath ? "0.52vw" : 0 }}>
                                                    No suggested folder path available.
                                                </div>

                                                {isEditingFolderPath && (
                                                    <button
                                                        type="button"
                                                        onClick={addFolderLevelAtEnd}
                                                        style={addButtonStyle}
                                                        disabled={isMovingFile}
                                                    >
                                                        + Add First Folder Level
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <div style={footerStyle}>
                            <button
                                type="button"
                                onClick={onHide}
                                disabled={isMovingFile}
                                style={{
                                    border: "0.05vw solid #d1d5db",
                                    background: "#ffffff",
                                    color: "#374151",
                                    borderRadius: "0.52vw",
                                    padding: "0.42vw 0.72vw",
                                    fontSize: "0.68vw",
                                    fontWeight: 700,
                                    cursor: isMovingFile ? "not-allowed" : "pointer",
                                    opacity: isMovingFile ? 0.65 : 1
                                }}
                            >
                                Close
                            </button>

                            <button
                                type="button"
                                disabled={isMoveButtonDisabled}
                                onClick={handleMoveAndRename}
                                style={{
                                    border: "none",
                                    background: isMoveButtonDisabled
                                        ? "#d1d5db"
                                        : "linear-gradient(rgb(28, 108, 108), rgb(28, 108, 108))",
                                    color: "#ffffff",
                                    borderRadius: "0.52vw",
                                    padding: "0.42vw 0.72vw",
                                    fontSize: "0.68vw",
                                    fontWeight: 800,
                                    cursor: isMoveButtonDisabled ? "not-allowed" : "pointer"
                                }}
                            >
                                {isMovingFile ? "Moving..." : "Move & Rename File"}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};